import React, { useState } from 'react';
import type { Event } from '@/types/event';
import { imageSrc } from '@/utils/event';

const EventImage: React.FC<{ event: Event }> = ({ event }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const date = event.event_date ? new Date(event.event_date) : null;

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const getFallbackImageUrl = () => {
    const title = encodeURIComponent(event.title?.substring(0, 20) || 'Event');
    return `https://placehold.co/600x400/ff7f00/white?text=${title}`;
  };

  const finalImageSrc = imageError ? getFallbackImageUrl() : imageSrc(event);

  return (
    <div className="relative w-full md:w-1/3 h-48 sm:h-56 md:h-auto">
      {/* Loading Skeleton */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading image...</div>
        </div>
      )}
      
      {/* Image */}
      <img
        src={finalImageSrc}
        alt={event.title}
        className={`w-full h-full object-cover md:object-cover transition-opacity duration-300 ${
          !imageLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
      />  
      
      {/* Date Badge */}
      <div className="absolute bottom-3 sm:bottom-4 md:bottom-5 left-3 sm:left-4 md:left-5 bg-black/45 text-white p-2 sm:p-3 rounded-lg border border-white/20 text-center min-w-10 sm:min-w-12">
        <div className="text-xs font-bold uppercase tracking-wide mb-0.5 text-orange-600">
          {date?.toLocaleDateString('en-US', { month: 'short' })}
        </div>
        <div className="text-2xl sm:text-3xl font-semibold leading-none">
          {date?.getDate().toString().padStart(2, '0')}
        </div>
      </div>                
      
      {/* Featured Badge */}
      {event.is_featured && (
        <span className="absolute top-3 sm:top-4 md:top-5 right-3 sm:right-4 md:right-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-2 sm:px-3 py-1 rounded-full uppercase text-xs sm:text-sm font-semibold shadow-lg">
          Featured
        </span>
      )}
    </div>
  );
};

export default EventImage;